import { Injectable, inject, signal } from '@angular/core';
import { DocumentService } from './documents.service';
import { IDocumentResponse } from '../../interfaces';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DocumentSendListService {
    private readonly documentService = inject(DocumentService);

    readonly items = signal<IDocumentResponse[]>([]);
    readonly loading = signal<boolean>(false);
    readonly error = signal<string | null>(null);
    readonly pagination = signal({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
    });

    private searchTerm = '';

    async load(page = 1, limit = 20): Promise<void> {
        this.loading.set(true);
        this.error.set(null);

        try {
            const response = await firstValueFrom(
                this.documentService.listarEnviados(page, limit, this.searchTerm)
            );

            this.items.set(response.data || []);
            this.pagination.set({
                page: response.page,
                limit: response.limit,
                total: response.total,
                totalPages: response.totalPages,
                hasNextPage: response.page < response.totalPages,
                hasPreviousPage: response.page > 1,
            });
        } catch (err: any) {
            this.error.set(err?.error?.message || err?.message || 'Erro ao carregar documentos');
            this.items.set([]);
        } finally {
            this.loading.set(false);
        }
    }

    async search(term: string): Promise<void> {
        this.searchTerm = term?.trim() || '';
        await this.load(1, this.pagination().limit);
    }

    async nextPage(): Promise<void> {
        const current = this.pagination();
        if (current.hasNextPage) {
            await this.load(current.page + 1, current.limit);
        }
    }

    async previousPage(): Promise<void> {
        const current = this.pagination();
        if (current.hasPreviousPage) {
            await this.load(current.page - 1, current.limit);
        }
    }

    async goToPage(page: number): Promise<void> {
        const current = this.pagination();
        await this.load(page, current.limit);
    }

    async changePageSize(limit: number): Promise<void> {
        await this.load(1, limit);
    }

    invalidateCache(): void {
        this.documentService.invalidateAllCache();
        this.load(this.pagination().page, this.pagination().limit);
    }
}
